/**
 * RÉTROCESSIONS — le menu `retro` de la maquette : « De boutique à boutique ». Cadrage §6/6bis.
 *
 * Ce que la page SERT : les rétrocessions du magasin (les deux sens), la création (ENVOI ou DEMANDE),
 * l'acceptation/refus d'une demande, l'expédition (prix figé au PMP, stock sorti), le contrôle à
 * l'arrivée puis la réception (stock entré au prix figé), le refus de colis (= écart de 100 %),
 * l'annulation (créateur, tant que rien n'est réceptionné), et l'ARBITRAGE des écarts par l'émetteur —
 * jamais partie (retour au stock) ou perdue en transit (démarque) : le système ne choisit pas.
 *
 * ⚠️ LES GESTES VIENNENT DU SERVEUR (`gestes`), dérivés du rôle et de l'état : l'écran n'en recalcule
 * aucun — deux calculs du même droit finiraient par en donner deux.
 */
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { confirm, Icon, Modal, showToast, Trans, useLatest } from '@orgazone/ui-web';
import { currentCurrency, formatMoney, parseMinor } from '@orgazone/money-format';
import { exporterCsv } from './export-csv';
import { api, type EtatDeRetrocession, type LigneDeRetro, type LigneDeRetourFournisseur, type NatureDeRetrocession, type RetroDetaillee, type RetroResume, type RetourFournisseurDetaille, type RetourFournisseurResume } from '../api';
import { signalerEcartsChanges } from '../compte-ecarts';
import { fieldLabel } from '../vocabulary';
import { rescale } from '@orgazone/core-money';
import { key, t, type Translatable } from '../i18n';

const MONO = 'var(--font-mono)';

/**
 * UN MONTANT TAPÉ → l'entier à l'échelle FINE (4 décimales), `null` si le champ est vide,
 * `undefined` si ce n'est pas un montant.
 *
 * 🔴 CETTE PAGE EN PORTAIT DEUX COPIES, ET L'UNE AVAIT PERDU L'ANTISLASH DE `\s` : elle retirait la
 *    LETTRE « s » au lieu des espaces. Un prix copié depuis un tableau — « 1 250,00 », avec sa fine
 *    insécable — était donc déclaré « illisible » sous les yeux de l'opérateur, pendant que la copie
 *    jumelle, dix cellules plus loin, l'acceptait. Deux fois la même règle finissent toujours par
 *    diverger (Forme A), et aucun type ne voit un antislash manquant dans une expression régulière.
 * ⚠️ `parseMinor` PORTE DÉJÀ TOUT LE GESTE — espaces fine et insécable, virgule française, échelle,
 *    refus NOMMÉ. Une troisième copie n'avait rien à apporter.
 * ⚠️ Le NÉGATIF est refusé ici et non par la primitive : un prix de cession négatif n'a pas de sens
 *    sur cette page, alors qu'un montant négatif en a partout ailleurs (un écart, un avoir).
 */
export function montantFin(txt: string): number | null | undefined {
  const lu = parseMinor(txt, { scale: 4 });
  if (lu.state === 'empty') return null;
  return lu.state === 'ok' && lu.minor >= 0 ? lu.minor : undefined;
}
/** Les 7 colonnes de la liste (maquette) — la colonne Contrôle (scan/directe) est OMISE : ce mode
 * n'existe pas dans notre modèle (la réception compte toujours le réel), l'offrir serait un mensonge. */
const GRILLE_LISTE = '148px minmax(190px,1.3fr) 64px 100px 178px 64px 74px';

/** Les états, vocabulaire RELEVÉ (`retStL`) — la demande se lit selon le rôle. */
function libelleEtat(r: RetroResume): string {
  if (r.resume.etat === 'dem') return r.direction === 'out' ? t('Demande à accepter') : t('En attente d’acceptation');
  // Hors plateforme, personne ne réceptionne : « en transit » serait une attente qui ne finit jamais.
  if (r.external && r.resume.etat === 'transit') return t('Expédiée — hors plateforme');
  return { prep: t('À expédier'), transit: t('En transit'), done: t('Reçue'), ref: t('Refusée'), anl: t('Annulée') }[r.resume.etat] ?? r.resume.etat;
}
const TON: Record<EtatDeRetrocession, { bg: string; fg: string }> = {
  // ⚠️ La palette RETST de la maquette, à l'identique : dem=ambre, prep=bleu, transit=VIOLET,
  // done=teal, ref=rose, anl=encre. La 1ʳᵉ version avait décalé trois teintes.
  dem: { bg: 'var(--amber-bg)', fg: 'var(--amber-fg)' },
  prep: { bg: 'var(--blue-bg)', fg: 'var(--blue-fg)' },
  transit: { bg: 'var(--violet-bg)', fg: 'var(--violet-fg)' },
  done: { bg: 'var(--teal-100)', fg: 'var(--teal-800)' },
  ref: { bg: 'var(--rose-bg)', fg: 'var(--rose-fg)' },
  anl: { bg: 'var(--ink-100)', fg: 'var(--ink-600)' },
};

const dateCourte = (ms: number | null): string => {
  if (ms == null) return '—';
  const d = new Date(ms);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const euroFin = (fine: number | null): string => (fine == null ? '—' : formatMoney(rescale(fine, 4, 2), { thousandsSep: ' ' }));

function StatusPill({ etat, texte }: { etat: EtatDeRetrocession; texte: string }) {
  // Le badge CARRÉ de la maquette (r-xs, 22 px, 10.5/800) — pas une pilule.
  const tone = TON[etat];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px', borderRadius: 'var(--r-xs)', background: tone.bg, color: tone.fg, fontSize: 'var(--t-sub)', fontWeight: 800, letterSpacing: '.02em', whiteSpace: 'nowrap' }}>{texte}</span>
  );
}
/**
 * Le TRAJET, vu d'ici — le carré fléché de la maquette : entrante = teal ←, sortante = bleu →,
 * libellé « depuis X » / « vers X ».
 */
function Trajet({ direction, partner }: { direction: 'out' | 'in'; partner: string }) {
  const out = direction === 'out';
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      <span aria-hidden="true" style={{
        width: 24, height: 24, borderRadius: 'var(--r-xs)', flexShrink: 0,
        background: out ? 'var(--blue-bg)' : 'var(--teal-100)',
        color: out ? 'var(--blue-fg)' : 'var(--teal-800)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d={out ? 'M5 12h14M13 6l6 6-6 6' : 'M20 12H4m0 0l4-4m-4 4l4 4'} />
        </svg>
      </span>
      <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-700)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {out ? t('vers {v}', { v: partner }) : t('depuis {v}', { v: partner })}
      </span>
    </span>
  );
}


/** Le BON DE RÉTROCESSION (A4) — il voyage avec le colis (maquette `retbon`). */
function imprimerBon(r: RetroDetaillee) {
  const esc = (v: unknown) => String(v ?? '').replace(/[&<>"]/g, (x) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[x] ?? x));
  const rows = r.lines.map((l) => `<tr><td>${esc(l.name ?? l.gencode)}${l.color ? ` · ${esc(l.color)}` : ''}</td><td class="m">${esc(l.gencode)}</td><td class="c">${esc(l.size ?? '—')}</td><td class="r">${l.qty}</td><td class="r"></td></tr>`).join('');
  // ⚠️ LES TEXTES SORTENT DU BALISAGE, ils ne s'y enveloppent pas : une clé qui porterait `<th>` ou
  //    `</style>` livrerait du HTML au traducteur, et traduire une balise casse le document. Le
  //    balisage reste donc LITTÉRAL, et chaque phrase entre par un trou — échappée comme le reste,
  //    parce qu'une traduction peut contenir une apostrophe typographique ou un chevron.
  const titre = t('Bon de rétrocession {v}', { v: r.number });
  const html = `<html><head><meta charset="utf-8"><title>${esc(titre)}</title><style>`
    + 'body{font:13px/1.5 system-ui;margin:36px;color:#131618}h1{font-size:19px;margin:0 0 2px}'
    + '.sub{color:#6c747c;margin:0 0 16px}table{width:100%;border-collapse:collapse;font-size:12px}'
    + 'th{text-align:left;border-bottom:2px solid #131618;padding:6px 8px;font-size:10px;text-transform:uppercase;letter-spacing:.04em}'
    + 'td{border-bottom:1px solid #e2e6ea;padding:6px 8px}.r{text-align:right}.c{text-align:center}.m{font-family:monospace;font-size:10px}'
    + '.foot{margin-top:18px;color:#97a0a8;font-size:10px}</style></head><body>'
    + `<h1>${esc(titre)}</h1>`
    + `<p class="sub">${esc(r.senderLabel ?? r.senderGln)} → ${esc(r.receiverLabel ?? r.receiverGln)} · ${esc(dateCourte(r.createdAt))}${r.note ? ` · ${esc(r.note)}` : ''}</p>`
    // ⚠️ LE MOT EST CELUI DU MAGASIN ÉMETTEUR : ce bon voyage vers un AUTRE magasin, qui compte contre ce
    //    papier avec le mot de l'expéditeur — son propre écran, lui, dit le sien. Assumé, pas oublié : le
    //    receveur reçoit le document, jamais le vocabulaire de l'autre base.
    + `<table><thead><tr><th>${esc(t('Article'))}</th><th>${esc(t('Code'))}</th><th style="text-align:center">${esc(fieldLabel('size'))}</th>`
    + `<th class="r">${esc(t('Envoyé'))}</th><th class="r">${esc(t('Compté à l’arrivée'))}</th></tr></thead>`
    + `<tbody>${rows}</tbody></table>`
    + `<p class="foot">${esc(t('Sliderz Back-office — ce bon voyage avec le colis : le receveur compte contre lui.'))}</p>`
    + '</body></html>';
  const f = document.createElement('iframe');
  // ⚠️ UNE FEUILLE DE STYLE NE SE TRADUIT PAS : `cssText` prend une suite de déclarations, pas
  //    une phrase. Traduite, la fenêtre d’impression deviendrait visible au milieu de l’écran.
  f.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  document.body.appendChild(f);
  f.contentDocument!.open(); f.contentDocument!.write(html); f.contentDocument!.close();
  f.contentWindow!.focus(); f.contentWindow!.print();
  window.setTimeout(() => f.remove(), 2000);
}

/** Les filtres de la maquette : un SENS (segmenté) et un ÉTAT (chips comptées). */
type DirFiltre = '' | 'in' | 'out';
type ChipFiltre = 'act' | 'transit' | 'dem' | 'fin' | '';

// ── LA PAGE ────────────────────────────────────────────────────────────────────────────────────
export function Retrocessions() {
  const [rows, setRows] = useState<RetroResume[] | null>(null);
  // ⚠️ UNION DÉLIBÉRÉE À L'AFFICHAGE (D39) : les retours fournisseur vivent dans une table à part
  // (achat négatif ≠ transfert, D25) — c'est l'écran qui les réunit, jamais une requête.
  const [retours, setRetours] = useState<RetourFournisseurResume[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [dir, setDir] = useState<DirFiltre>('');
  const [chip, setChip] = useState<ChipFiltre>('act');
  // `?open=<id>` : le centre d'écarts renvoie ici pour TRANCHER un écart — la modale s'ouvre d'emblée
  // (l'état « à traiter » de l'écart se dérive des lignes non arbitrées : c'est ici qu'il se règle).
  const [ouverte, setOuverte] = useState<string | null>(() => new URLSearchParams(window.location.search).get('open'));
  const [creation, setCreation] = useState(false);

  const reload = useCallback(async () => {
    try {
      setFailure(null);
      const [r, sr] = await Promise.all([api.retros(), api.supplierReturns()]);
      setRows(r.rows); setRetours(sr.rows);
    } catch (e) { setFailure(e instanceof Error ? e.message : String(e)); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  const aFaire = (r: RetroResume) => r.gestes.accept || r.gestes.ship || r.gestes.receive || r.gestes.arbitrate;
  /** La rangée UNIFIÉE : rétrocession OU retour fournisseur, triés ensemble par date. */
  type Rangee = { type: 'retro'; r: RetroResume } | { type: 'sr'; r: RetourFournisseurResume };
  const rangees = useMemo<Rangee[]>(() => {
    if (!rows) return [];
    const tout: Rangee[] = [
      ...rows.map((r) => ({ type: 'retro' as const, r })),
      ...retours.map((r) => ({ type: 'sr' as const, r })),
    ];
    return tout.sort((a, b) => b.r.createdAt - a.r.createdAt);
  }, [rows, retours]);
  const dansDir = useCallback((d: DirFiltre, x: Rangee): boolean => {
    if (d === '') return true;
    // Un retour fournisseur est toujours SORTANT — les pièces partent d'ici.
    const direction = x.type === 'retro' ? x.r.direction : 'out';
    return direction === d;
  }, []);
  const dansChip = useCallback((k: ChipFiltre, x: Rangee): boolean => {
    if (x.type === 'retro') {
      if (k === 'act') return aFaire(x.r);
      if (k === 'transit') return x.r.resume.etat === 'transit';
      if (k === 'dem') return x.r.resume.etat === 'dem';
      if (k === 'fin') return ['done', 'ref', 'anl'].includes(x.r.resume.etat);
      return true;
    }
    if (k === 'act') return x.r.gestes.ship;
    if (k === 'fin') return x.r.etat !== 'prep';
    if (k === '') return true;
    return false; // un retour fournisseur n'est jamais « en transit » ni une « demande »
  }, []);
  const parDir = useMemo(() => rangees.filter((x) => dansDir(dir, x)), [rangees, dir, dansDir]);
  const visibles = useMemo(() => parDir.filter((x) => dansChip(chip, x)), [parDir, chip, dansChip]);

  const SEGS: { v: DirFiltre; l: string }[] = [
    { v: '', l: t('Toutes') }, { v: 'in', l: t('← Entrantes') }, { v: 'out', l: t('→ Sortantes') },
  ];
  const CHIPS: { v: ChipFiltre; l: string }[] = [
    { v: 'act', l: t('À traiter') }, { v: 'transit', l: t('En transit') }, { v: 'dem', l: t('Demandes') },
    { v: 'fin', l: t('Terminées') }, { v: '', l: t('Toutes') },
  ];

  /** Export de la vue courante — les colonnes d'ici, la mécanique partagée (`export-csv`). */
  const exporter = () => {
    if (visibles.length === 0) return;
    exporterCsv(
      'retrocessions',
      ['N°', t('Nature'), t('Sens'), t('Partenaire'), t('Pièces'), t('Valeur PMP ({code})', { code: currentCurrency().code }), t('Statut'), t('Date')],
      visibles.map((x) => (x.type === 'retro'
        ? [x.r.number, x.r.kind === 'env' ? t('Envoi volontaire') : t('Demande'),
          x.r.direction === 'out' ? t('Sortante') : t('Entrante'),
          x.r.direction === 'out' ? (x.r.receiverLabel ?? x.r.receiverGln) : (x.r.senderLabel ?? x.r.senderGln),
          x.r.resume.qty, euroFin(x.r.resume.valueFine), libelleEtat(x.r), dateCourte(x.r.createdAt)]
        : [x.r.number, t('Retour fournisseur'), t('Sortante'), x.r.supplierLabel ?? '—',
          x.r.qty, euroFin(x.r.valueFine), t(ETATS_SR[x.r.etat].l), dateCourte(x.r.createdAt)])),
    );
    showToast(t('{n} ligne exportée', { n: visibles.length }), 'ok');
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('Rétrocessions')}</h1>
          <p style={{ margin: '3px 0 0', fontSize: '13.5px', color: 'var(--ink-500)' }}>
            <Trans k='De boutique à boutique — <1>sortie à l’expédition, entrée à la réception</1>, au PMP de l’émetteur.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></p>
        </div>
        <button type="button" onClick={exporter}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 40, padding: '0 18px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: '13.5px', fontWeight: 600, cursor: 'pointer' }}
        ><Icon name="download" size={15} />  {t('Export Excel')}</button>
        <button type="button" onClick={() => setCreation(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 38, padding: '0 16px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-900)', color: 'var(--paper)', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
        ><Icon name="plus" size={15} />  {t('Nouvelle rétrocession')}</button>
      </div>

      {failure ? (
        <div role="alert" style={{ padding: '16px 18px', border: '1px solid var(--danger-ink)', borderRadius: 'var(--r-md)', color: 'var(--danger-ink)', fontSize: '13px' }}>
          
          {t('Les rétrocessions n’ont pas pu être lues :')} {failure}
          <button type="button" className="btn-ghost" onClick={() => void reload()} style={{ marginLeft: 12 }}>{t('Réessayer')}</button>
        </div>
      ) : !rows ? (
        <p style={{ color: 'var(--ink-500)', fontSize: '13px' }}>{t('Lecture…')}</p>
      ) : (
        <>
          <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', overflow: 'hidden' }}>
            {/* La barre de filtres VIT DANS LA CARTE, comme la maquette : le sens en segmenté,
                l'état en chips comptées (l'active en teal foncé). */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: '1px solid var(--ink-200)', flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', overflow: 'hidden' }}>
                {SEGS.map((x) => (
                  <button aria-pressed={dir === x.v} key={x.v || 'toutes'} type="button" onClick={() => setDir(x.v)}
                    style={{
                      height: 36, padding: '0 12px', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                      background: dir === x.v ? 'var(--ink-100)' : 'transparent',
                      color: dir === x.v ? 'var(--ink-800)' : 'var(--ink-500)', fontSize: 'var(--t-dense)', fontWeight: 700,
                    }}>{x.l}</button>
                ))}
              </span>
              {CHIPS.map((c) => {
                const on = chip === c.v;
                const n = parDir.filter((x) => dansChip(c.v, x)).length;
                return (
                  <button aria-pressed={on} key={c.v || 'toutes'} type="button" onClick={() => setChip(c.v)}
                    style={{
                      height: 32, padding: '0 12px', borderRadius: 'var(--r-pill)', cursor: 'pointer', whiteSpace: 'nowrap',
                      border: `1px solid ${on ? 'var(--teal-800)' : 'var(--ink-200)'}`,
                      background: on ? 'var(--teal-800)' : 'var(--paper)',
                      color: on ? '#fff' : 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 700,
                    }}>{c.l} <span style={{ opacity: 0.65, fontSize: 'var(--t-sub)', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{n}</span></button>
                );
              })}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: 900 }}>
                <div style={{
                  display: 'grid', gridTemplateColumns: GRILLE_LISTE, gap: 10, padding: '9px 16px',
                  background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)', fontSize: '10.5px', fontWeight: 700,
                  letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)',
                }}>
                  <span>{t('N° · Origine')}</span><span>{t('Trajet')}</span>
                  <span style={{ textAlign: 'right' }}>{t('Pièces')}</span><span style={{ textAlign: 'right' }}>{t('Valeur PMP')}</span>
                  <span>{t('Statut')}</span><span>{t('Date')}</span><span />
                </div>
                {visibles.map((x) => x.type === 'sr' ? (
                  <RangeeRetour key={x.r.id} r={x.r} onOuvre={() => setOuverte(x.r.id)} />
                ) : (((r) => (
                  // ⚠️ PAS DE `role="button"` SUR LA LIGNE : elle porte son bouton « Traiter / Voir », que le rôle rendrait
                  //    présentationnel — invisible pour une aide technique. Le clavier passe par lui, la souris par la ligne.
                  <div key={r.id}
                    onClick={() => setOuverte(r.id)}
                    style={{
                      display: 'grid', gridTemplateColumns: GRILLE_LISTE, gap: 10, padding: '9px 16px',
                      alignItems: 'center', borderBottom: '1px solid var(--ink-100)', fontSize: '12.5px', cursor: 'pointer',
                    }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontFamily: MONO, fontSize: '11.5px', fontWeight: 700, color: 'var(--ink-900)' }}>{r.number}</div>
                      <div style={{ fontSize: '10.5px', color: 'var(--ink-400)' }}>{r.kind === 'env' ? t('Envoi volontaire') : t('Demande')}</div>
                    </div>
                    <Trajet direction={r.direction} partner={r.direction === 'out' ? (r.receiverLabel ?? r.receiverGln ?? '—') : (r.senderLabel ?? r.senderGln)} />
                    <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'var(--ink-900)' }}>{r.resume.qty}</div>
                    <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--ink-600)' }}>{euroFin(r.resume.valueFine)}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                      <StatusPill etat={r.resume.etat} texte={libelleEtat(r)} />
                      {r.resume.unresolvedVariances > 0 && (
                        <span style={{ height: 20, padding: '0 7px', borderRadius: 'var(--r-xs)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: '9.5px', fontWeight: 800, display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>{t('ÉCART À ARBITRER')}</span>
                      )}
                    </div>
                    <div style={{ fontSize: '11.5px', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>{dateCourte(r.createdAt)}</div>
                    <div style={{ textAlign: 'right' }}>
                      {/* ⚠️ GESTE EXPLICITE, alors que la rangée entière est déjà cliquable : sans lui, ce bouton
                          ne vit QUE par la remontée de l événement — un stopPropagation ajouté demain le tuerait en
                          silence, et il porte pourtant un curseur de main et un verbe d action. */}
                      <button type="button" onClick={() => setOuverte(r.id)}
                        style={{ height: 30, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: '11.5px', fontWeight: 700, cursor: 'pointer' }}
                      >{aFaire(r) ? t('Traiter') : t('Voir')}</button>
                    </div>
                  </div>
                ))(x.r)))}
                {visibles.length === 0 && (
                  <div style={{ padding: 30, textAlign: 'center', fontSize: '13px', color: 'var(--ink-500)' }}>
                    {rangees.length === 0
                      ? <>{t('Aucune rétrocession —')} <button type="button" onClick={() => setCreation(true)} style={{ border: 'none', background: 'none', padding: 0, color: 'var(--teal-800)', fontSize: '13px', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>{t('créez la première')}</button>  {t(': le document vit dans les deux magasins, le stock ne bouge qu’à l’expédition et à la réception.')}</>
                      : t('Aucune rétrocession dans cette vue.')}
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Relevé mot pour mot. */}
          <div style={{ marginTop: 9, fontSize: '12px', color: 'var(--ink-500)', lineHeight: 1.6 }}>
            
            {t('Annulable jusqu’à la réception — après, le stock est entré : on repart par une rétrocession retour.')}
          </div>
        </>
      )}

      {creation && (
        <NouvelleRetro onClose={() => setCreation(false)} onCree={(id) => { setCreation(false); void reload(); setOuverte(id); }} />
      )}
      {ouverte && (ouverte.startsWith('sr:')
        ? <RetourModal id={ouverte} onClose={() => { setOuverte(null); void reload(); }} />
        : <RetroModal id={ouverte} onClose={() => { setOuverte(null); void reload(); }} />)}
    </>
  );
}

// ── CRÉER — envoi ou demande, boutique partenaire, lignes ────────────────────────────────────────
function NouvelleRetro({ onClose, onCree }: { onClose: () => void; onCree: (id: string) => void }) {
  /** Le préfixe des identifiants qui relient chaque champ au texte qui le présente. */
  const fieldId = useId();
  const [partenaires, setPartenaires] = useState<{ gln: string; name: string }[] | null>(null);
  const [carnet, setCarnet] = useState<{ label: string; extGln: string | null }[]>([]);
  const [enseigneConnue, setEnseigneConnue] = useState(true);
  const [kind, setKind] = useState<NatureDeRetrocession>('env');
  const [partner, setPartner] = useState('');
  // Trois destinations (D36/D38/D39) : l'enseigne, le hors-plateforme, le FOURNISSEUR (retour).
  const [dest, setDest] = useState<'enseigne' | 'externe' | 'fournisseur'>('enseigne');
  const externe = dest === 'externe';
  const [maisons, setMaisons] = useState<{ code: string; label: string }[] | null>(null);
  const [fournCode, setFournCode] = useState('');
  const [fournLibre, setFournLibre] = useState('');
  const [labelExt, setLabelExt] = useState('');
  const [glnExt, setGlnExt] = useState('');
  const [paCession, setPaCession] = useState('');
  const [note, setNote] = useState('');
  const [rows, setLignes] = useState<{ gencode: string; qty: number; unitPriceFine?: number }[]>([]);
  const [code, setCode] = useState('');
  const [qte, setQte] = useState('1');
  const [erreur, setErreur] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);

  useEffect(() => {
    api.retroPartners().then((r) => { setPartenaires(r.rows); setCarnet(r.external); setEnseigneConnue(r.tenantKnown); }).catch((e) => { setPartenaires([]); setErreur(e instanceof Error ? e.message : String(e)); });
    api.referentielSuppliers().then((r) => setMaisons(r.rows.map((f) => ({ code: f.code, label: f.label })))).catch(() => setMaisons([]));
  }, []);

  const finDeCession = montantFin;
  const ajouter = () => {
    const g = code.trim();
    const q = Number(qte);
    if (!g) return;
    if (!Number.isInteger(q) || q < 1) { setErreur(t('Quantité : un entier, 1 au minimum.')); return; }
    const cession = dest !== 'enseigne' ? finDeCession(paCession) : null;
    if (cession === undefined) { setErreur(dest === 'fournisseur' ? t('Coût d’origine illisible — par exemple « 30,00 », ou vide (retrouvé dans vos achats).') : t('Prix de cession illisible — par exemple « 25,00 », ou vide (le PMP figera à l’expédition).')); return; }
    setErreur(null);
    setLignes((ls) => {
      const i = ls.findIndex((l) => l.gencode === g);
      if (i >= 0) { const c = [...ls]; c[i] = { ...c[i]!, qty: c[i]!.qty + q }; return c; }
      return [...ls, { gencode: g, qty: q, ...(dest !== 'enseigne' && cession != null ? { unitPriceFine: cession } : {}) }];
    });
    setCode(''); setQte('1'); setPaCession('');
  };

  const create = useCallback(async () => {
    if (dest === 'fournisseur') {
      const maison = maisons?.find((f) => f.code === fournCode) ?? null;
      const label = maison?.label ?? fournLibre.trim();
      if (!label) { setErreur(t('Nommez le fournisseur — un retour sans destinataire ne part nulle part.')); return; }
      if (rows.length === 0) { setErreur(t('Ajoutez au moins une ligne.')); return; }
      if (writing) return;
      setWriting(true);
      try {
        const doc = await api.createSupplierReturn({ supplierId: maison?.code ?? null, supplierLabel: label, note: note.trim() || null, lines: rows });
        showToast(t('Retour {number} créé — coût d’origine pré-rempli, à expédier.', { number: doc.number }));
        onCree(doc.id);
      } catch (e) { setErreur(e instanceof Error ? e.message : String(e)); setWriting(false); }
      return;
    }
    if (externe && !labelExt.trim()) { setErreur(t('Nommez le destinataire — le bon qui voyage avec le colis doit dire pour qui il est.')); return; }
    if (!externe && !partner) { setErreur(t('Choisissez la boutique partenaire.')); return; }
    if (rows.length === 0) { setErreur(t('Ajoutez au moins une ligne.')); return; }
    if (writing) return;
    setWriting(true);
    try {
      const doc = externe
        ? await api.createRetro({ external: true, partnerLabel: labelExt.trim(), partnerExtGln: glnExt.trim() || null, note: note.trim() || null, lines: rows })
        : await api.createRetro({ kind, partnerGln: partner, note: note.trim() || null, lines: rows });
      showToast(externe ? t('Rétrocession {number} créée — hors plateforme, à expédier.', { number: doc.number }) : kind === 'env' ? t('Rétrocession {number} créée — à expédier.', { number: doc.number }) : t('Demande {number} envoyée — en attente d’acceptation.', { number: doc.number }));
      onCree(doc.id);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e));
      setWriting(false);
    }
  }, [dest, maisons, fournCode, fournLibre, externe, labelExt, glnExt, kind, partner, note, rows, writing, onCree]);

  const champ = { width: '100%', height: 40, padding: '0 12px', boxSizing: 'border-box' as const, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '13.5px', outline: 'none' };
  const etiquette = { display: 'block', fontSize: '11px', fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase' as const, color: 'var(--ink-500)', marginBottom: 7 };

  // Les deux natures — tuiles relevées sur le wizard de la maquette.
  const KINDS: { v: NatureDeRetrocession; t: string; s: string }[] = [
    { v: 'env', t: t('Envoyer des pièces'), s: t('Vous expédiez vers la boutique choisie — le stock sort d’ici à l’expédition.') },
    { v: 'dem', t: t('Demander des pièces'), s: t('La boutique choisie devra accepter, puis expédier — le stock entrera ici à la réception.') },
  ];

  return (
    <Modal open onClose={onClose} title={t('Nouvelle rétrocession')} icon="outbound" width="lg"
      subtitle={t('Le document naît dans les deux magasins — l’annonce précède la marchandise.')}
      footer={(
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>{t('Annuler')}</button>
          <button type="button" onClick={() => void create()} aria-disabled={writing || undefined}
            style={{ height: 40, padding: '0 16px', border: 'none', borderRadius: 'var(--r-sm)', background: 'var(--ink-900)', color: 'var(--paper)', fontSize: '13.5px', fontWeight: 700, cursor: writing ? 'progress' : 'pointer' }}
          >{writing ? t('Création…') : dest === 'fournisseur' ? t('Créer le retour — à expédier') : kind === 'env' ? t('Créer — à expédier') : t('Envoyer la demande')}</button>
        </>
      )}>
      <div style={{ display: 'grid', gap: 16 }}>
        {dest === 'fournisseur' ? (
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--ink-600)', lineHeight: 1.6, padding: '10px 13px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)' }}>
            <Trans k='<1>Un retour fournisseur annule un achat</1> — il sort au <2>coût d’origine</2> (retrouvé dans vos achats, corrigeable), jamais au PMP. À l’expédition : « Expédié — avoir attendu ».' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></p>
        ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {KINDS.filter((k) => !externe || k.v === 'env').map((k) => {
            const on = kind === k.v;
            return (
              <button aria-pressed={on} key={k.v} type="button" onClick={() => setKind(k.v)}
                style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '14px 15px', border: `1.5px solid ${on ? 'var(--teal-400)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-sm)', background: on ? 'var(--teal-100)' : 'var(--paper)', textAlign: 'left', cursor: 'pointer' }}>
                <span style={{ fontSize: '14px', fontWeight: 800, color: 'var(--ink-900)' }}>{k.t}</span>
                <span style={{ fontSize: '12px', color: 'var(--ink-600)', lineHeight: 1.5 }}>{k.s}</span>
              </button>
            );
          })}
        </div>
        )}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {([['enseigne', t('Dans l’enseigne')], ['externe', t('Hors plateforme')], ['fournisseur', t('Retour fournisseur')]] as const).map(([v, l]) => {
            const on = dest === v;
            return (
              <button key={l} type="button" onClick={() => { setDest(v); if (v !== 'enseigne') setKind('env'); }}
                style={{ height: 32, padding: '0 13px', borderRadius: 'var(--r-pill)', border: `1px solid ${on ? 'var(--teal-400)' : 'var(--ink-200)'}`, background: on ? 'var(--teal-100)' : 'var(--paper)', color: on ? 'var(--teal-800)' : 'var(--ink-700)', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer' }}>{l}</button>
            );
          })}
        </div>

        {dest === 'fournisseur' ? (
          <div>
            <span id={`${fieldId}-0`} style={etiquette}>{fieldLabel('supplier')}</span>
            {maisons && maisons.length > 0 ? (
              <select aria-labelledby={`${fieldId}-0`} value={fournCode} onChange={(e) => setFournCode(e.target.value)} style={champ}>
                <option value="">{t('— choisir dans le référentiel —')}</option>
                {maisons.map((f) => <option key={f.code} value={f.code}>{f.label}</option>)}
              </select>
            ) : null}
            {!fournCode && (
              <input value={fournLibre} onChange={(e) => setFournLibre(e.target.value)} placeholder={maisons?.length ? t('ou un fournisseur hors référentiel') : t('Nom du fournisseur')}
                style={{ ...champ, marginTop: maisons?.length ? 8 : 0 }} />
            )}
          </div>
        ) : externe ? (
          <div style={{ display: 'grid', gap: 10 }}>
            <span style={etiquette}><Trans k='Destinataire hors plateforme <1>— autre système, magasin inconnu, partenaire</1>' parts={[(x) => <span style={{ textTransform: 'none', fontWeight: 500, letterSpacing: 0, color: 'var(--ink-400)' }}>{x}</span>]} /></span>
            {carnet.length > 0 && (
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                {carnet.map((c) => {
                  const on = labelExt === c.label;
                  return (
                    <button key={c.label} type="button" onClick={() => { setLabelExt(on ? '' : c.label); setGlnExt(on ? '' : (c.extGln ?? '')); }}
                      style={{ height: 30, padding: '0 11px', borderRadius: 'var(--r-pill)', border: `1px solid ${on ? 'var(--teal-400)' : 'var(--ink-200)'}`, background: on ? 'var(--teal-100)' : 'var(--paper)', color: on ? 'var(--teal-800)' : 'var(--ink-700)', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>{c.label}</button>
                  );
                })}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 10 }}>
              <input value={labelExt} onChange={(e) => setLabelExt(e.target.value)} placeholder={t('Nom du destinataire — mémorisé pour les envois suivants')} style={champ} />
              <input value={glnExt} onChange={(e) => setGlnExt(e.target.value)} placeholder={t('GLN — facultatif')} style={{ ...champ, fontFamily: MONO }} />
            </div>
            <p style={{ margin: 0, fontSize: '11.5px', color: 'var(--ink-500)', lineHeight: 1.5 }}>
              
              {t('Envoi seul (un système absent ne répond pas) — personne ne compte en face : l’état s’arrête à « Expédiée — hors plateforme », le bon imprimé voyage avec le colis. Prix de cession par ligne facultatif ; sans lui, le PMP fige à l’expédition.')}
            </p>
          </div>
        ) : (
        <div>
          <span style={etiquette}>{t('Boutique partenaire')}</span>
          {partenaires === null ? (
            <p style={{ margin: 0, fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Lecture du périmètre…')}</p>
          ) : partenaires.length === 0 ? (
            // ⚠️ HONNÊTE, et le motif est le BON : la liste vient de l'ENSEIGNE (même tenant), posée par
            // la déclaration Console — un magasin créé dans l'enseigne apparaîtra ici AUTOMATIQUEMENT.
            <p style={{ margin: 0, fontSize: '12.5px', color: 'var(--ink-500)', lineHeight: 1.5 }}>
              {enseigneConnue
                ? t('L’enseigne n’a pas d’autre boutique — un magasin créé dans l’enseigne apparaîtra ici automatiquement, sans autre réglage.')
                : t('Ce magasin n’est pas encore rattaché à son enseigne côté back-office : re-déclarez-le depuis la Console (fiche magasin → déclaration BO), puis revenez.')}
            </p>
          ) : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {partenaires.map((s) => {
                const on = partner === s.gln;
                return (
                  <button key={s.gln} type="button" onClick={() => setPartner(on ? '' : s.gln)}
                    style={{ height: 34, padding: '0 13px', borderRadius: 'var(--r-pill)', border: `1px solid ${on ? 'var(--teal-400)' : 'var(--ink-200)'}`, background: on ? 'var(--teal-100)' : 'var(--paper)', color: on ? 'var(--teal-800)' : 'var(--ink-700)', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer' }}>{s.name}</button>
                );
              })}
            </div>
          )}
        </div>
        )}

        <div>
          <span style={etiquette}><Trans k='Note <1>— facultative</1>' parts={[(x) => <span style={{ textTransform: 'none', fontWeight: 500, letterSpacing: 0, color: 'var(--ink-400)' }}>{x}</span>]} /></span>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('Réassort vitrine, demande cliente…')} style={champ} />
        </div>

        <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', gap: 8, padding: '10px 13px', borderBottom: '1px solid var(--ink-100)', alignItems: 'center', flexWrap: 'wrap' }}>
            <input value={code} onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ajouter(); } }}
              placeholder={t('Code-barres de l’article')} style={{ ...champ, flex: 1, minWidth: 170, height: 36, fontFamily: MONO }} />
            <input value={qte} onChange={(e) => setQte(e.target.value)} aria-label={t('Quantité')} style={{ ...champ, width: 70, height: 36, textAlign: 'center' }} />
            {dest !== 'enseigne' && (
              <input value={paCession} onChange={(e) => setPaCession(e.target.value)} placeholder={dest === 'fournisseur' ? t('Coût HT') : t('Cession HT')}
                aria-label={dest === 'fournisseur' ? t('Coût d’origine HT — facultatif, sinon retrouvé dans vos achats') : t('Prix de cession HT — facultatif')}
                style={{ ...champ, width: 96, height: 36, textAlign: 'right', fontFamily: MONO }} />
            )}
            <button type="button" onClick={ajouter}
              style={{ height: 36, padding: '0 14px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-900)', color: 'var(--paper)', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>{t('Ajouter la ligne')}</button>
          </div>
          {erreur && (
            <div role="alert" style={{ padding: '9px 13px', borderBottom: '1px solid var(--ink-100)', background: 'var(--rose-bg)', fontSize: '12px', fontWeight: 600, color: 'var(--rose-fg)' }}>{erreur}</div>
          )}
          {rows.length === 0 && (
            <div style={{ padding: '16px 13px', fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Aucune ligne — les codes doivent exister au catalogue de ce magasin.')}</div>
          )}
          {rows.map((l) => (
            <div key={l.gencode} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 13px', borderBottom: '1px solid var(--ink-100)', fontSize: '12.5px' }}>
              <span style={{ flex: 1, minWidth: 0, fontFamily: MONO, fontSize: '11.5px', color: 'var(--ink-800)' }}>{l.gencode}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>× {l.qty}</span>
              <button type="button" aria-label={t('Retirer {gencode}', { gencode: l.gencode })} onClick={() => setLignes((ls) => ls.filter((x) => x.gencode !== l.gencode))}
                style={{ width: 26, height: 26, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)', background: 'var(--paper)', color: 'var(--ink-500)', cursor: 'pointer' }}>×</button>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}

// ── LE DÉTAIL — les gestes du serveur, rien d'autre ──────────────────────────────────────────────
function RetroModal({ id, onClose }: { id: string; onClose: () => void }) {
  const [doc, setDoc] = useState<RetroDetaillee | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  // ⚠️ UNE SEULE PORTE POUR LA LECTURE ET LES GESTES : la lecture d'ouverture, revenue après un geste, reposerait l'état
  //    d'AVANT lui — l'écran proposerait de refaire ce qui vient d'être fait, et le serveur refuserait.
  const latest = useLatest();
  const reload = useCallback(async () => {
    const isLatest = latest();
    try {
      const lu = await api.retro(id);
      if (!isLatest()) return;
      setDoc(lu); setFailure(null);
    } catch (e) { if (isLatest()) setFailure(e instanceof Error ? e.message : String(e)); }
  }, [id, latest]);
  useEffect(() => { void reload(); }, [reload]);

  const agir = useCallback(async (fn: () => Promise<RetroDetaillee>, toast?: string) => {
    if (occupe) return;
    const isLatest = latest();
    setOccupe(true);
    try {
      const fait = await fn();
      if (isLatest()) setDoc(fait);
      if (toast) showToast(toast);
      // ⚠️ Une réception avec écart en OUVRE un, un arbitrage en FERME un : le rail porte la pastille
      //    du nombre d'écarts ouverts, elle mentirait en restant sur le chiffre d'avant (§23.0).
      signalerEcartsChanges();
    } catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err'); }
    finally { setOccupe(false); }
  }, [occupe, latest]);

  if (failure) {
    return (
      <Modal open onClose={onClose} title={t('Rétrocession')} width="md">
        <p role="alert" style={{ margin: 0, color: 'var(--danger-ink)', fontSize: '13px' }}>{t('La rétrocession n’a pas pu être lue : {failure}', { failure })}</p>
      </Modal>
    );
  }
  if (!doc) return <Modal open onClose={onClose} title={t('Rétrocession')} width="md"><p style={{ margin: 0, color: 'var(--ink-500)' }}>{t('Lecture…')}</p></Modal>;

  const g = doc.gestes;
  const enTransit = doc.resume.etat === 'transit' && doc.direction === 'in';
  const compteTotal = doc.lines.filter((l) => l.known).every((l) => l.checkedQty != null);
  const GRID = enTransit ? 'minmax(0,1.3fr) 52px 72px 148px 72px 92px' : 'minmax(0,1.3fr) 52px 72px 72px 72px 92px';

  const expedier = () => agir(async () => {
    const ok = await confirm({
      title: t('Expédier {number} ?', { number: doc.number }),
      message: t('{n} pièce sort du stock au PMP du moment — le prix est FIGÉ et vaudra des deux côtés. Imprimez le bon : il voyage avec le colis.', { n: doc.resume.qty }),
      confirmLabel: t('Expédier — stock sorti'),
    });
    if (!ok) throw new Error('—');
    return api.retroShip(doc.id);
  }, t('Expédiée — stock sorti, en transit'));

  const recevoir = () => agir(async () => {
    const r = await api.retroReceive(doc.id);
    showToast(r.variance > 0
      ? t('Réception validée — {n} pièce entrée · écart de {variance} remonté à l’émetteur', { n: r.pieces, variance: r.variance })
      : t('Réception validée — {n} pièce entrée en stock', { n: r.pieces }));
    // ⚠️ UN ARCHIVÉ QUI REVIENT SE DIT : il redescend en caisse, et personne ne saurait pourquoi sinon.
    if (r.restoredModels > 0) showToast(t('{n} modèle archivé remis en service : de la marchandise vient d’entrer pour lui.', { n: r.restoredModels }));
    return r.doc;
  });

  const cancel = () => agir(async () => {
    const ok = await confirm({
      title: t('Annuler {number} ?', { number: doc.number }),
      message: doc.shippedAt != null
        ? t('Le colis est en transit : les pièces reviennent dans le stock de l’émetteur par une entrée de correction tracée. Le document reste visible des deux côtés.')
        : t('Rien n’est parti — le document passe à Annulée et reste visible des deux côtés.'),
      confirmLabel: t('Annuler la rétrocession'),
    });
    if (!ok) throw new Error('—');
    return api.retroCancel(doc.id);
  }, t('Rétrocession annulée'));

  const refuser = (colis: boolean) => agir(async () => {
    const ok = await confirm({
      title: colis ? t('Refuser le colis {number} ?', { number: doc.number }) : t('Refuser la demande {number} ?', { number: doc.number }),
      message: colis
        ? t('Rien n’entre en stock — un refus est un écart de 100 %, il remonte à l’émetteur qui tranchera. La trace demeure.')
        : t('La demande passe à Refusée des deux côtés — refuser n’est jamais supprimer.'),
      confirmLabel: t('Refuser'),
    });
    if (!ok) throw new Error('—');
    return colis ? api.retroRefuseShipment(doc.id, null) : api.retroRefuseRequest(doc.id, null);
  });

  const note = {
    dem: doc.direction === 'out' ? t('Cette boutique vous demande des pièces : acceptez (vous préparerez l’envoi) ou refusez — la trace demeure.') : t('Votre demande attend l’acceptation de la boutique partenaire.'),
    prep: doc.direction === 'out' ? t('À expédier : imprimez le bon (il voyage avec le colis), puis expédiez — le stock sort et le prix se fige à cet instant.') : t('La boutique partenaire prépare l’envoi.'),
    transit: doc.external ? t('Expédiée hors plateforme — personne ne compte en face : l’envoi est terminal. Annulable si le colis revient (les pièces rentrent par une entrée de correction).')
      : doc.direction === 'in' ? t('Comptez le colis contre le bon — chaque ligne, ou « Tout est conforme ». La réception entre le COMPTÉ, jamais l’annoncé.') : t('En transit vers la boutique partenaire — annulable tant que rien n’est réceptionné.'),
    done: doc.resume.variance > 0 ? t('Reçue avec écart — l’émetteur tranche ligne à ligne : jamais partie (retour au stock) ou perdue en transit (démarque).') : t('Reçue — tout ce qui était annoncé est entré au prix figé.'),
    ref: t('Refusée — un refus est un écart de 100 % : l’émetteur tranche le sort des pièces.'),
    anl: t('Annulée — le document reste visible des deux côtés, rien n’a été effacé.'),
  }[doc.resume.etat];

  return (
    <Modal open onClose={onClose} title={doc.number} width="xl" icon="outbound"
      subtitle={`${doc.senderLabel ?? doc.senderGln} → ${doc.receiverLabel ?? doc.receiverGln} · ${dateCourte(doc.createdAt)}${doc.note ? ` · ${doc.note}` : ''}`}
      meta={<span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
        {doc.external && <span style={{ height: 20, padding: '0 8px', borderRadius: 'var(--r-xs)', background: 'var(--violet-bg)', color: 'var(--violet-fg)', fontSize: '9.5px', fontWeight: 800, display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>{t('HORS PLATEFORME')}</span>}
        <Trajet direction={doc.direction} partner={doc.direction === 'out' ? (doc.receiverLabel ?? doc.receiverGln ?? '—') : (doc.senderLabel ?? doc.senderGln)} /><StatusPill etat={doc.resume.etat} texte={libelleEtat(doc)} />
      </span>}
      footer={(
        <>
          {g.refuseRequest && <button type="button" className="btn-ghost" onClick={() => void refuser(false)}>{t('Refuser')}</button>}
          {g.accept && (
            <button type="button" onClick={() => void agir(() => api.retroAccept(doc.id), t('Demande acceptée — à préparer puis expédier'))}
              style={{ height: 40, padding: '0 16px', border: 'none', borderRadius: 'var(--r-sm)', background: 'var(--teal-800)', color: '#fff', fontSize: '13.5px', fontWeight: 700, cursor: 'pointer' }}>{t('Accepter — je prépare')}</button>
          )}
          {g.ship && <button type="button" className="btn-ghost" onClick={() => imprimerBon(doc)}>{t('Imprimer le bon')}</button>}
          {g.ship && (
            <button type="button" onClick={() => void expedier()}
              style={{ height: 40, padding: '0 16px', border: 'none', borderRadius: 'var(--r-sm)', background: 'var(--ink-900)', color: 'var(--paper)', fontSize: '13.5px', fontWeight: 700, cursor: 'pointer' }}>{t('Expédier — stock sorti')}</button>
          )}
          {g.refuseShipment && <button type="button" className="btn-ghost" onClick={() => void refuser(true)}>{t('Refuser le colis')}</button>}
          {enTransit && !compteTotal && (
            <button type="button" className="btn-ghost"
              onClick={() => void agir(() => api.retroCheck(doc.id, doc.lines.filter((l) => l.known).map((l) => ({ gencode: l.gencode, checkedQty: l.qty }))), t('Comptés posés à l’annoncé — vérifiez, puis validez'))}
            >{t('Tout est conforme')}</button>
          )}
          {g.receive && (
            <button type="button" aria-disabled={!compteTotal || undefined}
              title={compteTotal ? t('Entre le compté en stock, au prix figé') : t('Comptez chaque ligne — ou « Tout est conforme »')}
              onClick={() => { if (compteTotal) void recevoir(); }}
              style={{ height: 40, padding: '0 16px', border: 'none', borderRadius: 'var(--r-sm)', background: compteTotal ? 'var(--ink-900)' : 'var(--ink-200)', color: compteTotal ? 'var(--paper)' : 'var(--ink-500)', fontSize: '13.5px', fontWeight: 700, cursor: compteTotal ? 'pointer' : 'not-allowed' }}>{t('Valider la réception')}</button>
          )}
          {g.cancel && <button type="button" className="btn-ghost" onClick={() => void cancel()}>{t('Annuler')}</button>}
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: '12px', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>
            {t('{n} pièce', { n: doc.resume.qty })}{doc.resume.valueFine != null ? ` · ${euroFin(doc.resume.valueFine)}` : ''}
          </span>
          <button type="button" onClick={onClose}
            style={{ height: 40, padding: '0 16px', border: '1px solid var(--ink-300)', borderRadius: 'var(--r-sm)', background: 'var(--paper)', color: 'var(--ink-800)', fontSize: '13.5px', fontWeight: 700, cursor: 'pointer' }}>{t('Fermer')}</button>
        </>
      )}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {doc.refusedReason && (
          <div style={{ padding: '10px 13px', borderRadius: 'var(--r-sm)', background: 'var(--rose-bg)', fontSize: '12.5px', color: 'var(--rose-fg)', fontWeight: 600 }}>
            {t('Motif du refus : {refusedReason}', { refusedReason: doc.refusedReason })}
          </div>
        )}
        <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflowX: 'auto' }}>
          <div style={{ minWidth: 680 }}>
            <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 8, padding: '9px 14px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)', fontSize: '10px', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
              <span>{t('Article')}</span><span style={{ textAlign: 'center' }}>{fieldLabel('size')}</span><span style={{ textAlign: 'right' }}>{t('Envoyé')}</span>
              <span style={{ textAlign: 'center' }}>{enTransit ? t('Compté') : t('Reçu')}</span>
              <span style={{ textAlign: 'right' }}>{enTransit ? t('Reçu') : t('Écart')}</span><span style={{ textAlign: 'right' }}>{t('Prix figé')}</span>
            </div>
            {doc.lines.map((l) => (
              <LigneRetro key={l.gencode} l={l} grid={GRID} enTransit={enTransit} doc={doc}
                onControle={(checkedQty) => void agir(() => api.retroCheck(doc.id, [{ gencode: l.gencode, checkedQty }]))}
                onArbitre={(resolution) => void agir(
                  () => api.retroArbitrate(doc.id, l.gencode, resolution),
                  resolution === 'recount' ? t('Écart arbitré — la pièce rentre au stock (entrée de correction)') : t('Écart arbitré — perte en transit, le stock ne revient pas'),
                )} />
            ))}
          </div>
        </div>
        <div style={{ padding: '11px 14px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', fontSize: '12px', color: 'var(--ink-600)', lineHeight: 1.6 }}>{note}</div>
      </div>
    </Modal>
  );
}

/** Une ligne — le contrôle du receveur en transit, l'arbitrage de l'émetteur après réception. */
function LigneRetro({ l, grid, enTransit, doc, onControle, onArbitre }: {
  l: LigneDeRetro; grid: string; enTransit: boolean; doc: RetroDetaillee;
  onControle: (checkedQty: number | null) => void;
  onArbitre: (resolution: 'recount' | 'loss') => void;
}) {
  const [saisie, setSaisie] = useState(l.checkedQty == null ? '' : String(l.checkedQty));
  useEffect(() => { setSaisie(l.checkedQty == null ? '' : String(l.checkedQty)); }, [l.checkedQty]);
  const variance = l.qty - l.receivedQty;
  const arbitrable = doc.gestes.arbitrate && variance > 0 && l.varianceResolution == null;
  // ⚠️ CHAQUE CONTRÔLE NOMME SA LIGNE : « Quantité comptée » se répète sur chaque déclinaison du transfert.
  const ligne = [l.name ?? l.gencode, l.color, l.size].filter(Boolean).join(' · ');
  const bouton = { width: 24, height: 28, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: '13px', fontWeight: 700, cursor: 'pointer' } as const;

  const validerSaisie = () => {
    const typed = saisie.trim();
    const v = typed === '' ? null : Number(typed);
    if (v !== null && (!Number.isInteger(v) || v < 0)) { showToast(t('Quantité comptée : un entier, ou vide pour « pas encore comptée ».'), 'warn'); return; }
    if (v !== l.checkedQty) onControle(v);
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: grid, gap: 8, padding: '8px 14px', alignItems: 'center', borderBottom: '1px solid var(--ink-100)', fontSize: '12.5px' }}>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.name ?? l.gencode}{l.color ? ` · ${l.color}` : ''}</span>
        <span style={{ display: 'block', fontFamily: MONO, fontSize: '10px', color: 'var(--ink-400)' }}>{l.gencode}</span>
        {(doc.resume.etat === 'done' || doc.resume.etat === 'ref') && variance > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
            <span style={{ height: 18, padding: '0 6px', borderRadius: 'var(--r-xs)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: '9.5px', fontWeight: 800, display: 'inline-flex', alignItems: 'center' }}>
              {l.varianceResolution === 'recount' ? t('ARBITRÉ — RENTRÉE AU STOCK') : l.varianceResolution === 'loss' ? t('ARBITRÉ — PERTE EN TRANSIT') : t('ÉCART −{variance} · EN TRANSIT', { variance })}
            </span>
            {arbitrable && (
              <>
                <button type="button" onClick={() => onArbitre('recount')} title={t('La pièce n’est jamais partie : entrée de correction, elle rentre au stock')}
                  style={{ height: 22, padding: '0 8px', border: '1px solid var(--teal-400)', borderRadius: 'var(--r-xs)', background: 'var(--paper)', color: 'var(--teal-800)', fontSize: '10px', fontWeight: 800, cursor: 'pointer' }}>{t('Jamais partie')}</button>
                <button type="button" onClick={() => onArbitre('loss')} title={t('Partie et perdue : démarque valorisée, le stock ne revient pas')}
                  style={{ height: 22, padding: '0 8px', border: '1px solid var(--rose)', borderRadius: 'var(--r-xs)', background: 'var(--paper)', color: 'var(--rose-fg)', fontSize: '10px', fontWeight: 800, cursor: 'pointer' }}>{t('Perdue en transit')}</button>
              </>
            )}
          </span>
        )}
      </span>
      <span style={{ textAlign: 'center', fontFamily: MONO, fontSize: '11.5px', color: 'var(--ink-600)' }}>{l.size ?? '—'}</span>
      <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--ink-600)' }}>{l.qty}</span>
      <span style={{ display: 'flex', justifyContent: 'center' }}>
        {enTransit ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <button type="button" aria-label={t('Retirer une pièce comptée — {ligne}', { ligne })} style={bouton} onClick={() => onControle(Math.max(0, (l.checkedQty ?? 0) - 1))}>−</button>
            <input value={saisie} onChange={(e) => setSaisie(e.target.value)} onBlur={validerSaisie}
              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
              aria-label={t('Quantité comptée — {ligne}', { ligne })} placeholder="—"
              style={{ width: 44, height: 30, textAlign: 'center', border: `1px solid ${l.checkedQty == null ? 'var(--amber)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-xs)', background: l.checkedQty == null ? 'var(--amber-bg)' : 'var(--paper)', color: 'var(--ink-900)', fontSize: '12.5px', fontWeight: 700, fontVariantNumeric: 'tabular-nums', outline: 'none' }} />
            <button type="button" aria-label={t('Ajouter une pièce comptée — {ligne}', { ligne })} style={bouton} onClick={() => onControle((l.checkedQty ?? 0) + 1)}>+</button>
          </span>
        ) : (
          <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: l.receivedQty > 0 ? 'var(--ink-900)' : 'var(--ink-300)' }}>{l.receivedQty}</span>
        )}
      </span>
      <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'var(--ink-600)' }}>
        {enTransit ? l.receivedQty : (doc.resume.etat === 'done' || doc.resume.etat === 'ref') && variance > 0 ? `−${variance}` : ''}
      </span>
      <span style={{ textAlign: 'right', fontFamily: MONO, fontSize: '11.5px', color: 'var(--ink-600)', fontVariantNumeric: 'tabular-nums' }}>{euroFin(l.unitPriceFine)}</span>
    </div>
  );
}


// ═══════════════════════════ RETOURS FOURNISSEUR (D13/D39) — même menu, données à part ═══════════
const ETATS_SR: Record<'prep' | 'shipped' | 'cancelled', { l: Translatable; bg: string; fg: string }> = {
  prep: { l: key('À expédier'), bg: 'var(--teal-100)', fg: 'var(--teal-800)' },
  shipped: { l: key('Expédié — avoir attendu'), bg: 'var(--blue-bg)', fg: 'var(--blue-fg)' },
  cancelled: { l: key('Annulé'), bg: 'var(--ink-100)', fg: 'var(--ink-600)' },
};

/** Une rangée de la liste unifiée pour un retour fournisseur — badge dédié, mêmes colonnes. */
function RangeeRetour({ r, onOuvre }: { r: RetourFournisseurResume; onOuvre: () => void }) {
  const tone = ETATS_SR[r.etat];
  return (
    // ⚠️ PAS DE `role="button"` SUR LA LIGNE : elle porte son bouton, que le rôle rendrait présentationnel. Le clavier
    //    passe par lui, la souris par la ligne.
    <div onClick={onOuvre}
      style={{
        display: 'grid', gridTemplateColumns: GRILLE_LISTE, gap: 10, padding: '9px 16px',
        alignItems: 'center', borderBottom: '1px solid var(--ink-100)', fontSize: '12.5px', cursor: 'pointer',
      }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: MONO, fontSize: '11.5px', fontWeight: 700, color: 'var(--ink-900)' }}>{r.number}</div>
        <div style={{ fontSize: '10.5px', color: 'var(--rose-fg)', fontWeight: 700 }}>{t('Retour fournisseur')}</div>
      </div>
      {/* Une sortie comme les autres — la nature rose du sous-libellé suffit à la distinguer. */}
      <Trajet direction="out" partner={r.supplierLabel ?? '—'} />
      <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'var(--ink-900)' }}>{r.qty}</div>
      <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--ink-600)' }}>{euroFin(r.valueFine)}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px', borderRadius: 'var(--r-xs)', background: tone.bg, color: tone.fg, fontSize: 'var(--t-sub)', fontWeight: 800, letterSpacing: '.02em', whiteSpace: 'nowrap' }}>{t(tone.l)}</span>
        {r.lignesSansPrix > 0 && r.etat === 'prep' && (
          <span style={{ height: 20, padding: '0 7px', borderRadius: 'var(--r-xs)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: '9.5px', fontWeight: 800, display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>{t('SANS COÛT')}</span>
        )}
      </div>
      <div style={{ fontSize: '11.5px', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>{dateCourte(r.createdAt)}</div>
      <div style={{ textAlign: 'right' }}>
        {/* ⚠️ Même raison qu à la liste unifiée : la rangée est cliquable, mais un bouton qui ne vit que
            par la remontée de l événement meurt au premier stopPropagation, sans rien dire. */}
        <button type="button" onClick={onOuvre}
          style={{ height: 30, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: '11.5px', fontWeight: 700, cursor: 'pointer' }}
        >{r.gestes.ship ? t('Traiter') : t('Voir')}</button>
      </div>
    </div>
  );
}

/** Le bon de retour (A4) — il voyage avec le colis, l'avoir se rapprochera dessus. */
function imprimerBonDeRetour(r: RetourFournisseurDetaille) {
  const esc = (v: unknown) => String(v ?? '').replace(/[&<>"]/g, (x) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[x] ?? x));
  const rows = r.lines.map((l) => `<tr><td>${esc(l.name ?? l.gencode)}${l.color ? ` · ${esc(l.color)}` : ''}</td><td class="m">${esc(l.gencode)}</td><td class="c">${esc(l.size ?? '—')}</td><td class="r">${l.qty}</td><td class="r">${l.unitPriceFine == null ? '—' : euroFin(l.unitPriceFine)}</td></tr>`).join('');
  // ⚠️ Même règle que le bon de rétrocession : le balisage reste littéral, les phrases entrent par un
  //    trou. Un `<th>` traduit ne serait plus une cellule de tableau.
  const titre = t('Retour fournisseur {v}', { v: r.number });
  const html = `<html><head><meta charset="utf-8"><title>${esc(titre)}</title><style>`
    + 'body{font:13px/1.5 system-ui;margin:36px;color:#131618}h1{font-size:19px;margin:0 0 2px}'
    + '.sub{color:#6c747c;margin:0 0 16px}table{width:100%;border-collapse:collapse;font-size:12px}'
    + 'th{text-align:left;border-bottom:2px solid #131618;padding:6px 8px;font-size:10px;text-transform:uppercase;letter-spacing:.04em}'
    + 'td{border-bottom:1px solid #e2e6ea;padding:6px 8px}.r{text-align:right}.c{text-align:center}.m{font-family:monospace;font-size:10px}'
    + '.foot{margin-top:18px;color:#97a0a8;font-size:10px}</style></head><body>'
    + `<h1>${esc(titre)}</h1>`
    + `<p class="sub">${esc(r.supplierLabel ?? '—')} · ${esc(dateCourte(r.createdAt))}${r.note ? ` · ${esc(r.note)}` : ''}</p>`
    // ⚠️ Le mot est celui du magasin émetteur : le fournisseur rapproche son avoir sur ce papier, il n'a
    //    pas d'autre vocabulaire que celui qu'on lui imprime.
    + `<table><thead><tr><th>${esc(t('Article'))}</th><th>${esc(t('Code'))}</th><th style="text-align:center">${esc(fieldLabel('size'))}</th>`
    + `<th class="r">${esc(t('Renvoyé'))}</th><th class="r">${esc(t('Coût d’origine HT'))}</th></tr></thead>`
    + `<tbody>${rows}</tbody></table>`
    + `<p class="foot">${esc(t('Sliderz Back-office — ce bon voyage avec le colis ; l’avoir fournisseur se rapprochera dessus.'))}</p>`
    + '</body></html>';
  const f = document.createElement('iframe');
  // ⚠️ UNE FEUILLE DE STYLE NE SE TRADUIT PAS : `cssText` prend une suite de déclarations, pas
  //    une phrase. Traduite, la fenêtre d’impression deviendrait visible au milieu de l’écran.
  f.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  document.body.appendChild(f);
  f.contentDocument!.open(); f.contentDocument!.write(html); f.contentDocument!.close();
  f.contentWindow!.focus(); f.contentWindow!.print();
  window.setTimeout(() => f.remove(), 2000);
}

/** Le détail d'un retour — coût d'origine par ligne (corrigeable avant expédition), expédier, annuler. */
function RetourModal({ id, onClose }: { id: string; onClose: () => void }) {
  const [retour, setRetour] = useState<RetourFournisseurDetaille | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  // ⚠️ UNE SEULE PORTE POUR LA LECTURE ET LES GESTES : la lecture d'ouverture, revenue après un geste, reposerait l'état
  //    d'AVANT lui — l'écran proposerait de refaire ce qui vient d'être fait, et le serveur refuserait.
  const latest = useLatest();
  const reload = useCallback(async () => {
    const isLatest = latest();
    try {
      const lu = await api.supplierReturn(id);
      if (!isLatest()) return;
      setRetour(lu); setFailure(null);
    } catch (e) { if (isLatest()) setFailure(e instanceof Error ? e.message : String(e)); }
  }, [id, latest]);
  useEffect(() => { void reload(); }, [reload]);

  const agir = useCallback(async (fn: () => Promise<RetourFournisseurDetaille>, toast?: string) => {
    if (occupe) return;
    const isLatest = latest();
    setOccupe(true);
    try {
      const fait = await fn();
      if (isLatest()) setRetour(fait);
      if (toast) showToast(toast);
    }
    catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err'); }
    finally { setOccupe(false); }
  }, [occupe, latest]);

  if (failure) {
    return (
      <Modal open onClose={onClose} title={t('Retour fournisseur')} width="md">
        <p role="alert" style={{ margin: 0, color: 'var(--danger-ink)', fontSize: '13px' }}>{t('Le retour n’a pas pu être lu : {failure}', { failure })}</p>
      </Modal>
    );
  }
  if (!retour) return <Modal open onClose={onClose} title={t('Retour fournisseur')} width="md"><p style={{ margin: 0, color: 'var(--ink-500)' }}>{t('Lecture…')}</p></Modal>;

  const tone = ETATS_SR[retour.etat];
  const expedier = () => agir(async () => {
    const ok = await confirm({
      title: t('Expédier le retour {number} ?', { number: retour.number }),
      message: t('{n} pièce sort en ANNULATION D’ACHAT, au coût d’origine — quantité et valeur quittent vos achats.{v} Imprimez le bon : l’avoir se rapprochera dessus.', { n: retour.qty, v: retour.lignesSansPrix > 0 ? ` ${t('⚠️ {n} ligne sans coût connu sortira sans prix.', { n: retour.lignesSansPrix })}` : '' }),
      confirmLabel: t('Expédier le retour'),
    });
    if (!ok) throw new Error('—');
    return api.shipSupplierReturn(retour.id);
  }, t('Retour expédié — avoir fournisseur attendu'));

  const cancel = () => agir(async () => {
    const ok = await confirm({
      title: t('Annuler le retour {number} ?', { number: retour.number }),
      message: retour.shippedAt != null
        ? t('Le colis est revenu : l’achat se RÉTABLIT — les pièces rentrent au même coût d’origine, la sortie d’origine reste tracée.')
        : t('Rien n’est parti — le retour passe à Annulé.'),
      confirmLabel: t('Annuler le retour'),
    });
    if (!ok) throw new Error('—');
    return api.cancelSupplierReturn(retour.id);
  }, t('Retour annulé'));

  const note = retour.etat === 'prep'
    ? t('À expédier : vérifiez les coûts d’origine (retrouvés dans vos achats — corrigez si le fournisseur a facturé autrement), imprimez le bon, puis expédiez.')
    : retour.etat === 'shipped'
      ? t('Expédié — l’achat est annulé au coût d’origine. L’avoir fournisseur se rapproche sur ce bon (chantier facturation à venir).')
      : t('Annulé — si le colis était parti, l’achat s’est rétabli au même coût.');

  const GRID = 'minmax(0,1.4fr) 56px 84px 130px 96px';

  return (
    <Modal open onClose={onClose} title={retour.number} width="xl" icon="outbound"
      subtitle={`${retour.supplierLabel ?? '—'} · ${dateCourte(retour.createdAt)}${retour.note ? ` · ${retour.note}` : ''}`}
      meta={<span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
        <span style={{ height: 20, padding: '0 8px', borderRadius: 'var(--r-xs)', background: 'var(--rose-bg)', color: 'var(--rose-fg)', fontSize: '9.5px', fontWeight: 800, display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>{t('RETOUR FOURNISSEUR')}</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', height: 24, padding: '0 10px', borderRadius: 'var(--r-pill)', background: tone.bg, color: tone.fg, fontSize: '11.5px', fontWeight: 700, whiteSpace: 'nowrap' }}>{t(tone.l)}</span>
      </span>}
      footer={(
        <>
          <button type="button" className="btn-ghost" onClick={() => imprimerBonDeRetour(retour)}>{t('Imprimer le bon')}</button>
          {retour.gestes.ship && (
            <button type="button" onClick={() => void expedier()}
              style={{ height: 40, padding: '0 16px', border: 'none', borderRadius: 'var(--r-sm)', background: 'var(--ink-900)', color: 'var(--paper)', fontSize: '13.5px', fontWeight: 700, cursor: 'pointer' }}>{t('Expédier le retour')}</button>
          )}
          {retour.gestes.cancel && <button type="button" className="btn-ghost" onClick={() => void cancel()}>{t('Annuler')}</button>}
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: '12px', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>
            {t('{n} pièce', { n: retour.qty })}{retour.valueFine != null ? ` · ${euroFin(retour.valueFine)}` : ''}
            {retour.lignesSansPrix > 0 && <> · <strong style={{ color: 'var(--amber-fg)' }}>{t('{lignesSansPrix} sans coût', { lignesSansPrix: retour.lignesSansPrix })}</strong></>}
          </span>
          <button type="button" onClick={onClose}
            style={{ height: 40, padding: '0 16px', border: '1px solid var(--ink-300)', borderRadius: 'var(--r-sm)', background: 'var(--paper)', color: 'var(--ink-800)', fontSize: '13.5px', fontWeight: 700, cursor: 'pointer' }}>{t('Fermer')}</button>
        </>
      )}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflowX: 'auto' }}>
          <div style={{ minWidth: 620 }}>
            <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 8, padding: '9px 14px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)', fontSize: '10px', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
              <span>{t('Article')}</span><span style={{ textAlign: 'center' }}>{fieldLabel('size')}</span><span style={{ textAlign: 'right' }}>{t('Renvoyé')}</span>
              <span style={{ textAlign: 'right' }}>{t('Coût d’origine HT')}</span><span style={{ textAlign: 'right' }}>{t('Origine du coût')}</span>
            </div>
            {retour.lines.map((l) => (
              <LigneRetour2 key={l.gencode} l={l} grid={GRID} editable={retour.gestes.setPrice}
                onPrix={(fin) => void agir(() => api.setSupplierReturnPrice(retour.id, l.gencode, fin))} />
            ))}
          </div>
        </div>
        <div style={{ padding: '11px 14px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', fontSize: '12px', color: 'var(--ink-600)', lineHeight: 1.6 }}>{note}</div>
      </div>
    </Modal>
  );
}

/** Une ligne du retour — le coût d'origine, son ORIGINE dite, corrigeable avant expédition. */
function LigneRetour2({ l, grid, editable, onPrix }: {
  l: LigneDeRetourFournisseur; grid: string; editable: boolean; onPrix: (fin: number | null) => void;
}) {
  const [prix, setPrix] = useState(l.unitPriceFine == null ? '' : (l.unitPriceFine / 10000).toFixed(2).replace('.', ','));
  useEffect(() => { setPrix(l.unitPriceFine == null ? '' : (l.unitPriceFine / 10000).toFixed(2).replace('.', ',')); }, [l.unitPriceFine]);
  const validate = () => {
    const fin = montantFin(prix);
    if (fin === undefined) { showToast(t('Coût d’origine HT attendu, par exemple « 30,00 » — vide = sans coût.'), 'warn'); return; }
    if (fin !== l.unitPriceFine) onPrix(fin);
  };
  const src = l.priceSource === 'purchase' ? { t: t('Retrouvé dans vos achats (dernier prix chez ce fournisseur, sinon le dernier tout court)'), l: t('ACHATS') }
    : l.priceSource === 'manual' ? { t: t('Corrigé à la main'), l: t('SAISI') } : { t: t('Aucun coût d’achat retrouvé — la ligne sortira sans prix'), l: '—' };
  const ligne = [l.name ?? l.gencode, l.color, l.size].filter(Boolean).join(' · ');
  return (
    <div style={{ display: 'grid', gridTemplateColumns: grid, gap: 8, padding: '8px 14px', alignItems: 'center', borderBottom: '1px solid var(--ink-100)', fontSize: '12.5px' }}>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.name ?? l.gencode}{l.color ? ` · ${l.color}` : ''}</span>
        <span style={{ display: 'block', fontFamily: MONO, fontSize: '10px', color: 'var(--ink-400)' }}>{l.gencode}</span>
      </span>
      <span style={{ textAlign: 'center', fontFamily: MONO, fontSize: '11.5px', color: 'var(--ink-600)' }}>{l.size ?? '—'}</span>
      <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{l.qty}</span>
      <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {editable ? (
          <input value={prix} onChange={(e) => setPrix(e.target.value)} onBlur={validate}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            aria-label={t('Coût d’origine HT — {ligne}', { ligne })} placeholder="—"
            style={{ width: 76, height: 30, textAlign: 'right', border: `1px solid ${l.unitPriceFine == null ? 'var(--amber)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-xs)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '11.5px', fontWeight: 600, fontFamily: MONO, outline: 'none' }} />
        ) : (
          <span style={{ fontFamily: MONO, fontSize: '11.5px', fontWeight: 600, color: 'var(--ink-600)', fontVariantNumeric: 'tabular-nums' }}>{euroFin(l.unitPriceFine)}</span>
        )}
      </span>
      <span title={src.t} style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <span style={{ height: 17, padding: '0 5px', borderRadius: 'var(--r-xs)', background: 'var(--ink-100)', color: 'var(--ink-500)', fontSize: '8.5px', fontWeight: 800, letterSpacing: '.03em', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap', cursor: 'help' }}>{src.l}</span>
      </span>
    </div>
  );
}
